// src/hooks/useDevToolsTests.ts

import { useCallback } from 'react'
import { devices } from '../data/devices'
import {
  EnhancedLighthouseMetrics,
  DetailedAccessibilityIssue,
  ComprehensiveSEOMetrics,
  SecurityMetrics,
} from '../types'

export const useDevToolsTests = (
  setPerformanceData: React.Dispatch<React.SetStateAction<Map<string, EnhancedLighthouseMetrics>>>,
  setAccessibilityData: React.Dispatch<
    React.SetStateAction<Map<string, DetailedAccessibilityIssue[]>>
  >,
  setSeoData: React.Dispatch<React.SetStateAction<Map<string, ComprehensiveSEOMetrics>>>,
  setSecurityData: React.Dispatch<React.SetStateAction<Map<string, SecurityMetrics>>>,
  setRunningTests: React.Dispatch<React.SetStateAction<Set<string>>>,
  setActiveMetricsDevice: React.Dispatch<React.SetStateAction<string | null>>,
  setActiveTab: React.Dispatch<
    React.SetStateAction<'performance' | 'accessibility' | 'seo' | 'security'>
  >
) => {
  const runAccessibilityTest = useCallback(
    async (deviceName: string) => {
      await new Promise(resolve => setTimeout(resolve, 2000))

      const possibleIssues: DetailedAccessibilityIssue[] = [
        {
          type: 'contrast',
          severity: 'serious',
          message: 'Text elements have insufficient color contrast ratio',
          element: 'button.primary, .text-muted',
          count: 8,
          impact: 'high',
          helpUrl: 'https://webaim.org/articles/contrast/',
        },
        {
          type: 'alt',
          severity: 'critical',
          message: 'Images missing alternative text',
          element: 'img',
          count: 5,
          impact: 'high',
          helpUrl: 'https://webaim.org/techniques/alttext/',
        },
        {
          type: 'heading',
          severity: 'moderate',
          message: 'Heading structure is not properly nested',
          element: 'h1, h3',
          count: 2,
          impact: 'medium',
          helpUrl: 'https://webaim.org/techniques/semanticstructure/',
        },
        {
          type: 'keyboard',
          severity: 'serious',
          message: 'Interactive elements not keyboard accessible',
          element: 'div[onclick]',
          count: 3,
          impact: 'high',
          helpUrl: 'https://webaim.org/techniques/keyboard/',
        },
        {
          type: 'aria',
          severity: 'moderate',
          message: 'Form elements missing proper labels',
          element: 'input, select',
          count: 4,
          impact: 'medium',
          helpUrl: 'https://webaim.org/techniques/forms/',
        },
      ]

      const issues = possibleIssues.filter(() => Math.random() > 0.3)
      setAccessibilityData(prev => new Map(prev).set(deviceName, issues))
    },
    [setAccessibilityData]
  )

  const runSEOTest = useCallback(
    async (deviceName: string) => {
      await new Promise(resolve => setTimeout(resolve, 2500))

      const titleLength = Math.round(35 + Math.random() * 30)
      const descLength = Math.round(120 + Math.random() * 40)
      const imageTotal = Math.round(8 + Math.random() * 12)
      const imageMissing = Math.round(Math.random() * 5)

      const seoMetrics: ComprehensiveSEOMetrics = {
        score: Math.round(70 + Math.random() * 25),
        // Basic SEO
        title: {
          exists: Math.random() > 0.1,
          length: titleLength,
          optimal: titleLength >= 30 && titleLength <= 60,
        },
        metaDescription: {
          exists: Math.random() > 0.2,
          length: descLength,
          optimal: descLength >= 120 && descLength <= 160,
        },
        headings: {
          h1Count: Math.round(Math.random() * 3),
          structure: Math.random() > 0.3,
          optimal: Math.random() > 0.4,
        },
        imageAlts: {
          total: imageTotal,
          missing: imageMissing,
          percentage: Math.round(((imageTotal - imageMissing) / imageTotal) * 100),
        },
        // Technical SEO
        canonicalUrl: Math.random() > 0.3,
        viewport: Math.random() > 0.1,
        https: Math.random() > 0.05,
        mobileFriendly: Math.random() > 0.2,
        pageSpeed: Math.round(60 + Math.random() * 35),
        // Content SEO
        wordCount: Math.round(300 + Math.random() * 1200),
        readabilityScore: Math.round(60 + Math.random() * 35),
        internalLinks: Math.round(3 + Math.random() * 12),
        externalLinks: Math.round(1 + Math.random() * 6),
        // Schema & Structure
        structuredData: Math.random() > 0.4,
        openGraph: Math.random() > 0.3,
        twitterCards: Math.random() > 0.5,
        breadcrumbs: Math.random() > 0.6,
        // Advanced
        xmlSitemap: Math.random() > 0.3,
        robotsTxt: Math.random() > 0.2,
        hreflang: Math.random() > 0.7,
        coreWebVitals: Math.random() > 0.4,
        issues: [
          'Meta description too short',
          'Missing H1 tag',
          'Images without alt text',
          'No canonical URL specified',
          'Missing OpenGraph tags',
          'Low text-to-HTML ratio',
          'Too many internal links',
          'Missing schema markup',
        ].filter(() => Math.random() > 0.6),
        recommendations: [
          'Optimize meta description length (120-160 characters)',
          'Add structured data markup',
          'Improve internal linking structure',
          'Compress and optimize images',
          'Add breadcrumb navigation',
          'Implement lazy loading for images',
          'Minify CSS and JavaScript',
          'Use descriptive anchor text',
        ].filter(() => Math.random() > 0.5),
      }

      setSeoData(prev => new Map(prev).set(deviceName, seoMetrics))
    },
    [setSeoData]
  )

  const runSecurityTest = useCallback(
    async (deviceName: string) => {
      await new Promise(resolve => setTimeout(resolve, 1800))

      const securityMetrics: SecurityMetrics = {
        score: Math.round(70 + Math.random() * 25),
        https: Math.random() > 0.1,
        hsts: Math.random() > 0.4,
        contentSecurityPolicy: Math.random() > 0.6,
        xFrameOptions: Math.random() > 0.3,
        mixedContent: Math.random() < 0.2,
        vulnerabilities: Math.round(Math.random() * 3),
      }

      setSecurityData(prev => new Map(prev).set(deviceName, securityMetrics))
    },
    [setSecurityData]
  )

  const runPerformanceTest = useCallback(
    async (deviceName: string) => {
      setRunningTests(prev => new Set(prev).add(deviceName))
      setActiveMetricsDevice(deviceName)
      setActiveTab('performance')

      await new Promise(resolve => setTimeout(resolve, 3000 + Math.random() * 2000))

      const device = devices.find(d => d.name === deviceName)
      const isMobile = device?.category === 'phone'
      const isTablet = device?.category === 'tablet'

      const basePerformance = isMobile ? 60 + Math.random() * 30 : 70 + Math.random() * 25
      const loadTimeBase = isMobile ? 3200 : isTablet ? 2800 : 2200

      const metrics: EnhancedLighthouseMetrics = {
        performance: Math.round(basePerformance),
        accessibility: Math.round(75 + Math.random() * 20),
        bestPractices: Math.round(80 + Math.random() * 15),
        seo: Math.round(85 + Math.random() * 12),
        pwa: Math.round(40 + Math.random() * 30),
        // Core Web Vitals
        loadTime: Math.round(loadTimeBase + Math.random() * 1500),
        firstContentfulPaint: Math.round(loadTimeBase * 0.4 + Math.random() * 800),
        largestContentfulPaint: Math.round(loadTimeBase * 0.7 + Math.random() * 1200),
        firstInputDelay: Math.round(80 + Math.random() * 150),
        cumulativeLayoutShift: Math.round((0.05 + Math.random() * 0.2) * 1000) / 1000,
        speedIndex: Math.round(loadTimeBase * 0.8 + Math.random() * 1000),
        totalBlockingTime: Math.round(100 + Math.random() * 300),
        // Additional metrics
        timeToInteractive: Math.round(loadTimeBase * 0.9 + Math.random() * 1000),
        maxPotentialFID: Math.round(120 + Math.random() * 200),
        serverResponseTime: Math.round(150 + Math.random() * 300),
        // Resource analysis
        totalSize: Math.round(1200 + Math.random() * 2800), // KB
        imageOptimization: Math.round(60 + Math.random() * 35),
        textCompression: Math.round(70 + Math.random() * 25),
        unusedCode: Math.round(15 + Math.random() * 35),
        renderBlocking: Math.round(5 + Math.random() * 15),
        // Network
        requestCount: Math.round(25 + Math.random() * 45),
        cacheHitRatio: Math.round(60 + Math.random() * 35),
        cdnUsage: Math.random() > 0.3,
      }

      setPerformanceData(prev => new Map(prev).set(deviceName, metrics))
      setRunningTests(prev => {
        const newSet = new Set(prev)
        newSet.delete(deviceName)
        return newSet
      })

      // Auto-run other tests
      setTimeout(() => runAccessibilityTest(deviceName), 500)
      setTimeout(() => runSEOTest(deviceName), 1000)
      setTimeout(() => runSecurityTest(deviceName), 1500)
    },
    [
      setPerformanceData,
      setRunningTests,
      setActiveMetricsDevice,
      setActiveTab,
      runAccessibilityTest,
      runSEOTest,
      runSecurityTest
    ]
  )

  return {
    runPerformanceTest,
    runAccessibilityTest,
    runSEOTest,
    runSecurityTest,
  }
}
